import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import LinkWithCountry from '../../components/LinkWithCountry/LinkWithCountry';

import Loader from '../../components/Loader/Loader';
import heroImage from './imgs/snack-madness-banner.png';
import heroImageMobile from './imgs/snack-madness-banner-mobile.png';
import styles from './SnackMadness.module.css';

import { routeCountryPath } from '../../shared/utilities/common';
import {
  SNACK_MADNESS_SWEEPSTAKES_TAG,
  SNACK_MADNESS_TERMS_PATH,
  isContestOpen,
  isContestClosed,
  getContestStartLabel,
} from '../../shared/constants/snackMadness';
import * as snackMadnessRepo from '../../shared/repos/graphql/snackMadness';

const CONTEST_CLOSED_LABEL = 'Contest Closed';
const SUBMIT_ENTRY_LABEL = 'Submit Entry';
const ENTRY_SUBMITTED_LABEL = 'Entry Submitted';
const SIGN_IN_JOIN_REWARDS_LABEL = 'Sign in / Join Rewards';

const SUCCESS_MODAL_TITLE = "The dream starts here.";
const SUCCESS_MODAL_BODY = "Your entry has been received for a shot at heading to Indianapolis for a weekend of sports, snacks and super fandom at the 2026 men's college basketball semifinals.";

const DEFAULT_SUBMIT_ERROR_MESSAGE = 'Something went wrong. Please try again.';
const CONTEST_UNAVAILABLE_MESSAGE = 'Contest is currently unavailable. Please try again later.';
const PAGE_TITLE = 'Insomnia Cookies | Win A Sweet Trip';

/**
 * Snack Madness Sweepstakes landing page.
 * Renders one of: pre-contest, contest closed, contest open (logged out), contest open (logged in).
 * Handles entry submission and success modal.
 */
class SnackMadness extends Component {
  constructor(props) {
    super(props);
    this.state = {
      sweepstakes: null,
      sweepstakesLoading: true,
      loading: false,
      entrySubmitted: false,
      checkingEntry: true,
      showSuccessModal: false,
      submitError: null,
    };
  }

  componentDidMount() {
    document.title = PAGE_TITLE;
    if (typeof navigator !== 'undefined' && navigator.platform && navigator.platform.indexOf('Mac') > -1) {
      document.body.classList.add('os-mac');
    }
    this.fetchSweepstakesAndEntryStatus();
  }

  componentWillUnmount() {
    document.body.classList.remove('os-mac');
  }

  /** Fetches sweepstakes by tag (open_date, close_date), then entry status if applicable. */
  fetchSweepstakesAndEntryStatus = async () => {
    try {
      const sweepstakes = await snackMadnessRepo.getSweepstakesByTag(SNACK_MADNESS_SWEEPSTAKES_TAG);
      this.setState({ sweepstakes, sweepstakesLoading: false }, () => {
        this.checkEntryStatus();
      });
    } catch {
      this.setState({ sweepstakes: null, sweepstakesLoading: false, checkingEntry: false });
    }
  };

  /** Fetches entry status when user is logged in and contest is open (uses sweepstakes dates from state). */
  checkEntryStatus = async () => {
    const { userToken } = this.props;
    const { sweepstakes } = this.state;
    const openDate = sweepstakes?.openDate;
    const closeDate = sweepstakes?.closeDate;
    if (!userToken || !sweepstakes || !isContestOpen(openDate, closeDate)) {
      this.setState({ checkingEntry: false });
      return;
    }
    try {
      const hasEntered = await snackMadnessRepo.getSnackMadnessEntryStatus(SNACK_MADNESS_SWEEPSTAKES_TAG);
      this.setState({ entrySubmitted: hasEntered, checkingEntry: false });
    } catch {
      this.setState({ checkingEntry: false });
    }
  };

  /** Returns one of: pre_contest | contest_closed | contest_open_logged_out | contest_open_logged_in | unavailable */
  getPageState = () => {
    const { sweepstakes } = this.state;
    const { userToken } = this.props;
    const openDate = sweepstakes?.openDate;
    const closeDate = sweepstakes?.closeDate;
    if (!sweepstakes || !openDate || !closeDate) return 'unavailable';

    const contestOpen = isContestOpen(openDate, closeDate);
    const isLoggedIn = Boolean(userToken);

    if (!contestOpen) {
      return isContestClosed(closeDate) ? 'contest_closed' : 'pre_contest';
    }
    if (!isLoggedIn) return 'contest_open_logged_out';
    return 'contest_open_logged_in';
  };

  /** Submits sweepstakes entry (backend uses auth customer_id; no email/phone sent). Shows success modal or error. */
  handleSubmitEntry = async () => {
    const { userToken } = this.props;
    const { entrySubmitted } = this.state;
    if (!userToken || entrySubmitted) return;

    this.setState({ loading: true, submitError: null });
    try {
      const result = await snackMadnessRepo.submitSnackMadnessEntry({
        sweepstakesTag: SNACK_MADNESS_SWEEPSTAKES_TAG,
      });

      if (result?.success) {
        this.setState({
          entrySubmitted: true,
          loading: false,
          showSuccessModal: true,
        });
      } else {
        this.setState({
          loading: false,
          submitError: result?.message || DEFAULT_SUBMIT_ERROR_MESSAGE,
        });
      }
    } catch (err) {
      this.setState({
        loading: false,
        submitError: err?.message || DEFAULT_SUBMIT_ERROR_MESSAGE,
      });
    }
  };

  closeSuccessModal = () => {
    this.setState({ showSuccessModal: false });
  };

  /** Renders CTA based on page state: pre-contest, closed, sign-in link, submit/entry-submitted, or unavailable. */
  renderCTA = () => {
    const pageState = this.getPageState();
    const { checkingEntry, sweepstakes, submitError } = this.state;

    if (pageState === 'unavailable') {
      return (
        <button type="button" className={styles.ctaButton} disabled>
          {CONTEST_UNAVAILABLE_MESSAGE}
        </button>
      );
    }

    if (pageState === 'pre_contest') {
      return (
        <button type="button" className={styles.ctaButton} disabled>
          {getContestStartLabel(sweepstakes?.openDate)}
        </button>
      );
    }

    if (pageState === 'contest_closed') {
      return (
        <button type="button" className={styles.ctaButton} disabled>
          {CONTEST_CLOSED_LABEL}
        </button>
      );
    }

    if (pageState === 'contest_open_logged_out') {
      const loginPath = routeCountryPath('/login?redirectUrl=/bracket');
      return (
        <Link to={loginPath} className={styles.ctaButton}>
          {SIGN_IN_JOIN_REWARDS_LABEL}
        </Link>
      );
    }

    // contest_open_logged_in
    const { entrySubmitted, loading } = this.state;
    if (checkingEntry) {
      return (
        <button type="button" className={styles.ctaButton} disabled>
          Loading...
        </button>
      );
    }

    if (entrySubmitted) {
      return (
        <button type="button" className={styles.ctaButton} disabled>
          {ENTRY_SUBMITTED_LABEL}
        </button>
      );
    }

    return (
      <>
        <button
          type="button"
          className={styles.ctaButton}
          disabled={loading}
          onClick={this.handleSubmitEntry}
        >
          {loading ? 'Submitting...' : SUBMIT_ENTRY_LABEL}
        </button>
        {submitError && (
          <p className={styles.errorText}>{submitError}</p>
        )}
      </>
    );
  };

  /** Renders success modal when entry is submitted. */
  renderSuccessModal = () => {
    const { showSuccessModal } = this.state;
    if (!showSuccessModal) return null;
    return (
      <div className={styles.modalOverlay} role="dialog" aria-modal="true" aria-labelledby="success-modal-title">
        <div className={styles.modalContent}>
          <h2 id="success-modal-title" className={styles.modalTitle}>{SUCCESS_MODAL_TITLE}</h2>
          <p className={styles.modalBody}>{SUCCESS_MODAL_BODY}</p>
          <div className={styles.modalButtonContainer}>
            <button type="button" className={styles.modalButton} onClick={this.closeSuccessModal}>
            Okay
            </button>
          </div>
        </div>
      </div>
    );
  };

  render() {
    const { sweepstakesLoading, loading } = this.state;

    if (sweepstakesLoading || loading) {
      return (
        <div className="w-full flex justify-center bg-dark min-h-screen">
          <div className="w-full">
            <Loader id="loader" />
          </div>
        </div>
      );
    }

    return (
      <div className="w-full min-h-screen dark:bg-dark bg-[#f6f3f7]">
        <div className={`${styles.heroWrapper} w-full ${styles.mxAuto} mb-4`}>
          <picture>
            <source media="(max-width: 767px)" srcSet={heroImageMobile} />
            <img src={heroImage} alt="Snack Madness" className={styles.heroImg} />
          </picture>
        </div>
        <section className={`${styles.sectionPadding} ${styles.sectionBg}`}>
          <div className={`${styles.maxW6xl} ${styles.mxAuto} px-4 ${styles.mdPx1}`}>
            <div className="mb-4 leading-[1.2] w-full">
              <h1 className={`${styles.headingFont} ${styles['text-5xl']} ${styles.headingColor} font-bold not-italic text-center md:text-left`}>
                Win a sweet trip to College Basketball&apos;s Biggest Weekend 
                {' '}
                <span role="img" aria-label="basketball">🏀</span>
              </h1>
              <p className={`py-4 pt-[22px] not-italic text-xl font-normal text-center md:text-left font-montserratMedium ${styles.textColor}`}>
                Two Insomniacs and a friend are headed to Indianapolis on April 4th for a weekend of sports, snacks and super fandom.
              </p>
              <div className="text-[#4a4a4a] text-sm md:text-[15px] mb-8 leading-relaxed w-full">
                <h2 className={`text-xl font-bold font-montserratMedium text-center md:text-left ${styles.headingColor}`}>
                Here&apos;s How to Enter:
                </h2>
                <div className={`text-xl ${styles.pt2} space-y-2`}>
                  <div className={styles.stepRow}>
                    <span
                      className={`${styles.stepRowBadge} inline-flex items-center justify-center ${styles.w7} ${styles.h7} min-w-[1.5rem] rounded-full ${styles.bgPurple} text-white text-sm font-bold font-montserratMedium`}
                    >
                      1
                    </span>
                    <p className={`${styles.stepRowText} ${styles.textSecondaryForeground} text-left font-montserratMedium ${styles.textColor} ${styles.mobilePadding}`}>
                      Join Insomnia Rewards (or log in if you are already a member)
                    </p>
                  </div>
                  <div className={styles.stepRow}>
                    <span
                      className={`${styles.stepRowBadge} inline-flex items-center justify-center ${styles.w7} ${styles.h7} min-w-[1.5rem] rounded-full ${styles.bgPurple} text-white text-sm font-bold font-montserratMedium`}
                    >
                      2
                    </span>
                    <p className={`${styles.stepRowText} ${styles.textSecondaryForeground} font-montserratMedium text-left ${styles.textColor}`}>
                      Submit your entry
                    </p>
                  </div>
                </div>
                <p className={`text-xl font-montserratMedium ${styles.pt30px} ${styles.pb5px} ${styles.textSecondaryForeground} text-center md:text-left ${styles.textColor}`}>
That&apos;s it! (We told you being a loyalty member would pay off&nbsp;
                  <span role="img" aria-label="smiling">😊</span>
)
                </p>
              </div>  
              

              <div className={styles.ctaSection}>
                {this.renderCTA()}
                <p className={styles.termsText}>
                  This Sweepstakes is not sponsored, endorsed, administered by, or otherwise associated with the
                  NCAA.&nbsp;
                  <LinkWithCountry to={SNACK_MADNESS_TERMS_PATH} className={styles.termsLink}>
                    View Terms &amp; Conditions
                  </LinkWithCountry>
                </p>
              </div>
            </div>  
          </div>
        </section>
        {this.renderSuccessModal()}
      </div>
    );
  }
}

SnackMadness.propTypes = {
  userToken: PropTypes.string,
};

SnackMadness.defaultProps = {
  userToken: null,
};

SnackMadness.displayName = 'SnackMadness';

export const mapStateToProps = (state) => ({
  userToken: state.user.userToken,
});

export const mapDispatchToProps = () => ({});

export { SnackMadness };
export default connect(mapStateToProps, mapDispatchToProps)(SnackMadness);
